import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import usePageTracking from './hooks/usePageTracking';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import MainLayout from './components/layout/MainLayout';
import PrivateRoute from './components/PrivateRoute';
import ToastContainer from './components/toast/ToastContainer';
import './App.css';

const HomePage = lazy(() => import('./pages/HomePage'));
const PostPage = lazy(() => import('./pages/PostPage'));
const CategoryPage = lazy(() => import('./pages/CategoryPage'));
const SubscriptionFeedPage = lazy(() => import('./pages/SubscriptionFeedPage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const RatingPage = lazy(() => import('./pages/RatingPage'));
const InfoLayout = lazy(() => import('./pages/InfoLayout'));
const InfoAboutPage = lazy(() => import('./pages/InfoAboutPage'));
const InfoRulesPage = lazy(() => import('./pages/InfoRulesPage'));
const InfoKarmaPage = lazy(() => import('./pages/InfoKarmaPage'));
const InfoUpdatesPage = lazy(() => import('./pages/InfoUpdatesPage'));
const UpdatePage = lazy(() => import('./pages/UpdatePage'));
const InfoReportPage = lazy(() => import('./pages/InfoReportPage'));
const DraftsPage = lazy(() => import('./pages/DraftsPage'));
const PostsManagePage = lazy(() => import('./pages/PostsManagePage'));
const BookmarksPage = lazy(() => import('./pages/BookmarksPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const ConfirmEmailPage = lazy(() => import('./pages/ConfirmEmailPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const EditPostPage = lazy(() => import('./pages/EditPostPage'));
const CreatePostPage = lazy(() => import('./pages/CreatePostPage'));
const Profile = lazy(() => import('./components/profile/Profile'));
const ChatPage = lazy(() => import('./pages/ChatPage'));
const ErrorPage = lazy(() => import('./pages/ErrorPage'));

function PageTracker() {
    usePageTracking();
    return null;
}

function AppWrapper() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <Router>
                    <PageTracker />
                    <ToastContainer />
                    <Suspense fallback={null}>
                        <Routes>
                            {/* Fullscreen — no layout */}
                            <Route path="*" element={<ErrorPage code={404} />} />

                            {/* Main layout */}
                            <Route element={<MainLayout />}>
                                <Route path="/" element={<HomePage />} />
                                <Route path="/popular" element={<HomePage />} />
                                <Route path="/new" element={<HomePage />} />
                                <Route path="/search" element={<SearchPage />} />
                                <Route path="/rating" element={<RatingPage />} />

                                <Route path="/info" element={<InfoLayout />}>
                                    <Route index element={<InfoAboutPage />} />
                                    <Route path="rules" element={<InfoRulesPage />} />
                                    <Route path="karma" element={<InfoKarmaPage />} />
                                    <Route path="updates" element={<InfoUpdatesPage />} />
                                    <Route path="report" element={<InfoReportPage />} />
                                </Route>
                                <Route path="/info/updates/:slug" element={<UpdatePage />} />
                                <Route path="/profile" element={<Profile />} />
                                <Route path="/profile/:username" element={<Profile />} />
                                <Route path="/user/:userId" element={<Profile />} />
                                <Route path="/confirm-email" element={<ConfirmEmailPage />} />
                                <Route path="/reset-password" element={<ResetPasswordPage />} />

                                <Route path="/feed" element={
                                    <PrivateRoute fallbackRedirect="/?modal=login">
                                        <SubscriptionFeedPage />
                                    </PrivateRoute>
                                } />
                                <Route path="/create-post" element={
                                    <PrivateRoute fallbackRedirect="/?modal=login">
                                        <CreatePostPage />
                                    </PrivateRoute>
                                } />
                                <Route path="/messages" element={
                                    <PrivateRoute>
                                        <ChatPage />
                                    </PrivateRoute>
                                } />
                                <Route path="/messages/:chatId" element={
                                    <PrivateRoute>
                                        <ChatPage />
                                    </PrivateRoute>
                                } />
                                <Route path="/drafts" element={
                                    <PrivateRoute fallbackRedirect="/?modal=login">
                                        <DraftsPage />
                                    </PrivateRoute>
                                } />
                                <Route path="/posts-manage" element={
                                    <PrivateRoute fallbackRedirect="/?modal=login">
                                        <PostsManagePage />
                                    </PrivateRoute>
                                } />
                                <Route path="/bookmarks" element={
                                    <PrivateRoute fallbackRedirect="/?modal=login">
                                        <BookmarksPage />
                                    </PrivateRoute>
                                } />
                                <Route path="/notifications" element={
                                    <PrivateRoute fallbackRedirect="/?modal=login">
                                        <NotificationsPage />
                                    </PrivateRoute>
                                } />
                                <Route path="/settings" element={
                                    <PrivateRoute fallbackRedirect="/?modal=login">
                                        <SettingsPage />
                                    </PrivateRoute>
                                } />
                                <Route path="/post/:postId/edit" element={
                                    <PrivateRoute fallbackRedirect="/?modal=login">
                                        <EditPostPage />
                                    </PrivateRoute>
                                } />

                                <Route path="/:slug" element={<CategoryPage />} />
                                <Route path="/:categorySlug/:slug" element={<PostPage />} />
                                <Route path="/user/:userId/:slug" element={<PostPage />} />
                            </Route>
                        </Routes>
                    </Suspense>
                </Router>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default AppWrapper;
